import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  isBacklogTicketStatus,
  isClosedTicketStatus,
  ticketDisplaySubject,
  type TicketSummary,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

type StatusFilter = 'open' | 'backlog' | 'closed' | 'all';

const STATUS_FILTERS: readonly StatusFilter[] = ['open', 'backlog', 'closed', 'all'];

function parseStatusFilter(raw: string | null): StatusFilter {
  return STATUS_FILTERS.find((f) => f === raw) ?? 'open';
}

export function PortalTicketsListPage(): React.ReactElement {
  useDocumentTitle('My Tickets');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const statusFilter = parseStatusFilter(searchParams.get('view'));
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  // The tab lives in the URL so the dashboard (and a client's own bookmark)
  // can deep-link straight to the backlog.
  const setStatusFilter = useCallback(
    (next: StatusFilter) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === 'open') p.delete('view');
          else p.set('view', next);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then(async (r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal/tickets' } });
          return;
        }
        setContact(r.contact);
        const tk = await apiFetch<{ tickets: TicketSummary[] }>('/api/portal/tickets');
        setTickets(tk.tickets);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          navigate('/login', { replace: true, state: { from: '/portal/tickets' } });
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load tickets.');
      });
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!tickets) return [];
    const term = search.trim().toLowerCase();
    return tickets.filter((t) => {
      const closed = isClosedTicketStatus(t.status);
      const backlog = isBacklogTicketStatus(t.status);
      if (statusFilter === 'open' && (closed || backlog)) return false;
      if (statusFilter === 'backlog' && !backlog) return false;
      if (statusFilter === 'closed' && !closed) return false;
      if (typeFilter !== 'all' && t.type !== typeFilter) return false;
      if (term) {
        const hay = `${t.clientTicketNumber ?? ''} ${t.subject} T-${t.displayNumber} ${t.status}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [tickets, statusFilter, typeFilter, search]);

  const counts = useMemo(() => {
    if (!tickets) return { open: 0, backlog: 0, closed: 0, all: 0 };
    let open = 0;
    let backlog = 0;
    let closed = 0;
    for (const t of tickets) {
      if (isClosedTicketStatus(t.status)) closed++;
      else if (isBacklogTicketStatus(t.status)) backlog++;
      else open++;
    }
    return { open, backlog, closed, all: tickets.length };
  }, [tickets]);

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} active="tickets" />

      <main className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/portal" className="text-sm text-[var(--color-muted)] hover:underline">
              ← Help center
            </Link>
            <h1 className="mt-1 font-display text-3xl font-bold text-[var(--color-header)]">
              My tickets
            </h1>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              {contact?.isOrgAdmin
                ? 'All tickets for your organization.'
                : 'Tickets you submitted, plus any you have permission to view.'}
            </p>
          </div>
          <Link
            to="/portal/tickets/new"
            className="rounded-full bg-[var(--color-header)] px-5 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            + Submit a request
          </Link>
        </div>

        {error && (
          <Card className="mt-6 border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </Card>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <FilterTab
            label={`Open (${counts.open})`}
            active={statusFilter === 'open'}
            onClick={() => setStatusFilter('open')}
          />
          <FilterTab
            label={`Backlog (${counts.backlog})`}
            active={statusFilter === 'backlog'}
            onClick={() => setStatusFilter('backlog')}
          />
          <FilterTab
            label={`Closed (${counts.closed})`}
            active={statusFilter === 'closed'}
            onClick={() => setStatusFilter('closed')}
          />
          <FilterTab
            label={`All (${counts.all})`}
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              <option value="all">All types</option>
              <option value="escalation">Escalation</option>
              <option value="project">Project</option>
              <option value="quote">Quote</option>
              <option value="chat">Chat</option>
            </select>
            <input
              type="search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            />
          </div>
        </div>

        {statusFilter === 'backlog' && (
          <Card className="mt-4 border-[var(--color-t3-purple)]/25 bg-[var(--color-t3-purple)]/[0.04]">
            <p className="text-sm text-[var(--color-body)]">
              <span className="font-semibold text-[var(--color-header)]">What is the backlog?</span>{' '}
              Requests we have reviewed and accepted, but that are queued for later rather than
              being worked right now — planned improvements, deferred fixes, and work waiting on a
              scheduled window. They are still open, and we will move them into active work and
              update you here. Need one pulled forward? Reply on the ticket and tell us.
            </p>
          </Card>
        )}

        <div className="mt-4">
          {tickets === null && !error ? (
            <p className="text-sm text-[var(--color-muted)]">Loading…</p>
          ) : filtered.length === 0 ? (
            <Card>
              <p className="text-center text-sm text-[var(--color-muted)]">
                {statusFilter === 'backlog' && !search && typeFilter === 'all' ? (
                  <>Nothing in the backlog — every open request is being actively worked.</>
                ) : (
                  <>
                    No tickets match the current filter.{' '}
                    <Link to="/portal/tickets/new" className="text-[var(--color-t3-blue)] hover:underline">
                      Submit a new request
                    </Link>
                    .
                  </>
                )}
              </p>
            </Card>
          ) : (
            <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Subject</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface)]/50">
                      <td className="px-4 py-3 font-mono text-xs">T-{t.displayNumber}</td>
                      <td className="px-4 py-3">
                        <Link to={`/portal/tickets/${t.id}`} className="text-[var(--color-t3-blue)] hover:underline">
                          {ticketDisplaySubject(t.clientTicketNumber, t.subject)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-xs uppercase tracking-wide text-[var(--color-muted)]">
                        {t.type}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={t.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted)]">
                        {new Date(t.updatedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <PortalFooter />
    </div>
  );
}

function FilterTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full px-3.5 py-1.5 text-xs font-semibold transition ' +
        (active
          ? 'bg-[var(--color-header)] text-white'
          : 'border border-[var(--color-border)] bg-white text-[var(--color-muted)] hover:text-[var(--color-header)]')
      }
    >
      {label}
    </button>
  );
}

function StatusPill({ status }: { status: string }): React.ReactElement {
  const map: Record<string, { bg: string; border: string; text: string }> = {
    new: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
    backlog: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800' },
    in_progress: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
    waiting_on_client: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
    client_responded: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800' },
    on_hold: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' },
    scheduled_session: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800' },
    solved: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800' },
    cancelled: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' },
    dead: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' },
  };
  const cls = map[status] ?? { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
  return (
    <span className={`shrink-0 rounded-full border ${cls.border} ${cls.bg} ${cls.text} px-2.5 py-0.5 text-xs font-medium`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}
